export type ConditionValue = string | string[]
export type Condition = { field: string; op: string; value?: ConditionValue }
export type MatchMode = 'all' | 'any'
export type ConditionSearch = { c?: string; match?: MatchMode }
export type ConditionField = {
  key: string
  type: 'text' | 'number' | 'money' | 'date' | 'choice' | 'record'
  choices?: { value: string; label: string }[]
  recordSource?: 'customer' | 'supplier' | 'partner' | 'journal' | 'analysis_tag' | 'cost_center'
}

export const OPERATORS: Record<ConditionField['type'], string[]> = {
  text: ['contains', 'not_contains', 'is', 'is_not', 'empty', 'not_empty'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty'],
  money: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty'],
  date: [
    'on',
    'before',
    'after',
    'between',
    'last_n_days',
    'this_month',
    'last_month',
    'this_quarter',
    'this_year',
    'empty',
  ],
  choice: ['any_of', 'none_of'],
  record: ['any_of', 'none_of', 'empty'],
}

const noValue = new Set([
  'empty',
  'not_empty',
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
])

/**
 * With no conditions the keys are present but undefined: routes spread this
 * over the current search, and an absent key would keep the old c.
 */
export function encodeConditions(conditions: Condition[], match: MatchMode): ConditionSearch {
  return conditions.length
    ? { c: JSON.stringify(conditions.map((item) => [item.field, item.op, item.value])), match }
    : { c: undefined, match: undefined }
}

/**
 * The c search param as the decoder expects it. The app writes it as a string,
 * but the router parses an unquoted JSON value in a hand-written link into an
 * array before validateSearch runs.
 */
export function conditionSearchParam(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return JSON.stringify(value)
  return undefined
}

export function decodeConditions(
  search: ConditionSearch,
  fields: ConditionField[],
): { conditions: Condition[]; match: MatchMode } {
  let parsed: unknown
  try {
    parsed = JSON.parse(search.c ?? '[]')
  } catch {
    parsed = []
  }
  const allowed = new Map(fields.map((field) => [field.key, field]))
  const conditions = Array.isArray(parsed)
    ? parsed.flatMap((row): Condition[] => {
        if (
          !Array.isArray(row) ||
          row.length < 2 ||
          typeof row[0] !== 'string' ||
          typeof row[1] !== 'string'
        )
          return []
        const field = allowed.get(row[0])
        if (!field || !OPERATORS[field.type].includes(row[1])) return []
        const op = row[1]
        const value = row[2]
        if (noValue.has(op)) return [{ field: field.key, op }]
        if (
          (op === 'between' || op === 'any_of' || op === 'none_of') &&
          (!Array.isArray(value) || !value.every((part) => typeof part === 'string'))
        )
          return []
        if (op !== 'between' && op !== 'any_of' && op !== 'none_of' && typeof value !== 'string')
          return []
        if (
          field.type === 'choice' &&
          Array.isArray(value) &&
          field.choices &&
          value.some((part) => !field.choices?.some((choice) => choice.value === part))
        )
          return []
        return [{ field: field.key, op, value: value as ConditionValue }]
      })
    : []
  return { conditions: conditions.slice(0, 10), match: search.match === 'any' ? 'any' : 'all' }
}

export function toConditionQuery(
  conditions: Condition[],
  match: MatchMode,
): Record<string, string> {
  const query: Record<string, string> = { match }
  conditions.forEach((condition, index) => {
    query[`conditions[${index}][field]`] = condition.field
    query[`conditions[${index}][op]`] = condition.op
    // Lists become indexed keys: the HTTP client would send an array as repeated
    // value=a&value=b keys, which PHP reads as the last value only.
    if (Array.isArray(condition.value)) {
      condition.value.forEach((part, partIndex) => {
        query[`conditions[${index}][value][${partIndex}]`] = part
      })
    } else if (condition.value !== undefined) {
      query[`conditions[${index}][value]`] = condition.value
    }
  })
  return conditions.length ? query : {}
}

export function conditionsChanged<
  T extends { conditions?: Condition[]; match?: MatchMode; page?: number },
>(next: T, previous: T): T {
  return JSON.stringify(next.conditions ?? []) !== JSON.stringify(previous.conditions ?? []) ||
    (next.match ?? 'all') !== (previous.match ?? 'all')
    ? { ...next, page: 1 }
    : next
}
